import { erpRequest } from "@/core/api/erp-client";
import type { DocumentDirection, DocumentWorkflowDirection } from "@/contracts";

export type ErpNotificationData = Record<string, string | undefined>;

export type ErpDocumentNotificationAction =
  | "OPEN_DOCUMENT"
  | "OPEN_INTERNAL_DOCUMENT";

export type ErpDocumentNotificationType =
  | "DOCUMENT_DELIVERED"
  | "DOCUMENT_PENDING_APPROVAL"
  | "DOCUMENT_APPROVED"
  | "DOCUMENT_REJECTED"
  | "DOCUMENT_ASSIGNED"
  | "DOCUMENT_ASSIGNMENT_REVOKED"
  | "DOCUMENT_ASSIGNMENT_ACCEPTED"
  | "DOCUMENT_PROCESSING_STARTED"
  | "DOCUMENT_COMPLETED"
  | "DOCUMENT_PENDING_PROCESSING"
  | "DOCUMENT_SENT_TO_HR"
  | "DOCUMENT_DISPATCHED";

export type ErpNotificationType =
  | ErpDocumentNotificationType
  | (string & Record<never, never>);

export type ErpNotificationCategory =
  | "ATTENDANCE"
  | "PROJECT"
  | "FINANCE"
  | "SALES"
  | "SYSTEM";

export type ErpNotification = {
  id: string;
  type: ErpNotificationType;
  category?: ErpNotificationCategory;
  title: string;
  body: string;
  data: ErpNotificationData;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
};

export type ErpNotificationPagination = {
  page: number;
  limit: number;
  total_items: number;
  total_pages: number;
};

export type ErpNotificationListResponse = {
  data: {
    items: ErpNotification[];
    pagination: ErpNotificationPagination;
  };
  message?: string;
};

type UnreadCountResponse = {
  data?: number | { count?: number; unread_count?: number };
  count?: number;
  unread_count?: number;
};

export const ERP_NOTIFICATION_QUERY_KEY = ["erp-notifications"] as const;
export const ERP_NOTIFICATION_UNREAD_QUERY_KEY = [
  "erp-notifications",
  "unread-count"
] as const;

export type ErpDocumentNotificationCounts = Record<
  DocumentWorkflowDirection,
  number
>;

export function listErpNotifications(params?: {
  page?: number;
  limit?: number;
  unread_only?: boolean;
  category?: ErpNotificationCategory;
  type?: string;
}) {
  const query = new URLSearchParams();
  if (params?.page) query.set("page", String(params.page));
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.unread_only !== undefined) {
    query.set("unread_only", String(params.unread_only));
  }
  if (params?.category) query.set("category", params.category);
  if (params?.type) query.set("type", params.type);

  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  return erpRequest<ErpNotificationListResponse>(`/erp/notifications${suffix}`);
}

async function listAllUnreadErpNotifications() {
  const firstPage = await listErpNotifications({
    page: 1,
    limit: 100,
    unread_only: true
  });
  const pages = firstPage.data.pagination.total_pages;
  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(pages - 1, 0) }, (_, index) =>
      listErpNotifications({
        page: index + 2,
        limit: 100,
        unread_only: true
      })
    )
  );

  return [firstPage, ...remainingPages].flatMap((page) => page.data.items);
}

export async function getErpUnreadDocumentNotificationCounts(): Promise<ErpDocumentNotificationCounts> {
  const notifications = await listAllUnreadErpNotifications();

  return notifications.reduce<ErpDocumentNotificationCounts>(
    (counts, notification) => {
      if (notification.data.action === "OPEN_INTERNAL_DOCUMENT") {
        counts.INTERNAL += 1;
        return counts;
      }
      const direction = notification.data.direction as DocumentDirection;
      if (
        notification.data.action === "OPEN_DOCUMENT" &&
        (direction === "INCOMING" || direction === "OUTGOING")
      ) {
        counts[direction] += 1;
      }
      return counts;
    },
    { INCOMING: 0, OUTGOING: 0, INTERNAL: 0 }
  );
}

export async function getErpUnreadDocumentIds(
  action: ErpDocumentNotificationAction
) {
  const notifications = await listAllUnreadErpNotifications();

  return [
    ...new Set(
      notifications
        .filter((notification) => notification.data.action === action)
        .map((notification) => notification.data.document_id)
        .filter((documentId): documentId is string => Boolean(documentId))
    )
  ];
}

export async function getErpNotificationUnreadCount() {
  const response = await erpRequest<UnreadCountResponse>(
    "/erp/notifications/unread-count"
  );
  const data = response.data;

  if (typeof data === "number") return data;
  if (typeof data?.unread_count === "number") return data.unread_count;
  if (typeof data?.count === "number") return data.count;
  if (typeof response.unread_count === "number") return response.unread_count;
  return response.count ?? 0;
}

export function markErpNotificationRead(notificationId: string) {
  return erpRequest(
    `/erp/notifications/${encodeURIComponent(notificationId)}/read`,
    {
      method: "POST"
    }
  );
}

export async function markUnreadErpDocumentNotificationsRead(
  documentId: string,
  action: ErpDocumentNotificationAction
) {
  const notificationIds = (await listAllUnreadErpNotifications())
    .filter(
      (notification) =>
        notification.data.action === action &&
        notification.data.document_id === documentId
    )
    .map((notification) => notification.id);

  await Promise.all(notificationIds.map(markErpNotificationRead));
  return notificationIds.length;
}

export function markAllErpNotificationsRead() {
  return erpRequest("/erp/notifications/read-all", { method: "POST" });
}

export function registerErpNotificationDevice(payload: {
  installation_id: string;
  fcm_token: string;
}) {
  return erpRequest("/erp/notifications/devices", {
    method: "PUT",
    body: JSON.stringify({ ...payload, platform: "web" })
  });
}

export function unregisterErpNotificationDevice(installationId: string) {
  return erpRequest("/erp/notifications/devices/unregister", {
    method: "POST",
    body: JSON.stringify({ installation_id: installationId })
  });
}
